import { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  Grow
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { PageContext, ThemeContext } from '../Context';
import { ReactComponent as BlueNoteLogo } from '../../assets/logo_bluenote.svg';

// Fenêtre de bienvenue affichée par-dessus le dashboard lors de la toute
// première connexion d'un utilisateur (juste après l'acceptation des CGU).
function WelcomeDialog() {
  const { setPage } = useContext(PageContext);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const landing = sessionStorage.getItem('postCguLanding');
    if (landing === 'welcome' || landing === 'tutorial') {
      sessionStorage.removeItem('postCguLanding');
      setOpen(true);
    }
  }, []);

  const handleGoToTutorial = () => {
    setOpen(false);
    setPage('Tutorial');
  };

  const handleStayOnDashboard = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleStayOnDashboard}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Grow}
      transitionDuration={400}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          background: isDark
            ? 'linear-gradient(145deg, #2c3e50 0%, #34495e 100%)'
            : 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
          boxShadow: isDark
            ? '0 20px 60px rgba(0, 0, 0, 0.6)'
            : '0 20px 60px rgba(26, 37, 60, 0.35)'
        }
      }}
      slotProps={{
        backdrop: {
          sx: { backdropFilter: 'blur(4px)', backgroundColor: 'rgba(15, 23, 42, 0.55)' }
        }
      }}
    >
      {/* Bandeau supérieur aux couleurs de l'application */}
      <Box
        sx={{
          background: isDark
            ? 'linear-gradient(135deg, rgb(37, 53, 100), rgb(52, 74, 141))'
            : 'linear-gradient(135deg, #1a253c, #2d407a)',
          px: 4,
          py: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Halos décoratifs */}
        <Box
          sx={{
            position: 'absolute',
            top: '-60px',
            right: '-40px',
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.45), transparent 70%)'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-70px',
            left: '-50px',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.3), transparent 70%)'
          }}
        />

        <BlueNoteLogo style={{ height: '56px', width: 'auto', color: 'white', zIndex: 1 }} />
        <Typography
          variant="h4"
          sx={{ color: 'white', fontWeight: 700, textAlign: 'center', zIndex: 1 }}
        >
          Bienvenue sur BlueNote
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: 'rgba(255, 255, 255, 0.85)', textAlign: 'center', zIndex: 1 }}
        >
          Votre nouvel outil de rédaction est prêt.
        </Typography>
      </Box>

      {/* Contenu */}
      <Box sx={{ px: 4, py: 3.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Typography
          variant="body1"
          sx={{ color: isDark ? '#e8ecf5' : '#333', textAlign: 'center', lineHeight: 1.6 }}
        >
          Vous vous trouvez actuellement sur le <b>tableau de bord</b>, votre espace de
          travail principal pour rédiger et suivre vos rapports.
        </Typography>

        {/* Astuce de navigation */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2.5,
            py: 2,
            borderRadius: '14px',
            background: isDark
              ? 'rgba(102, 126, 234, 0.12)'
              : 'rgba(102, 126, 234, 0.08)',
            border: isDark
              ? '1px solid rgba(102, 126, 234, 0.35)'
              : '1px solid rgba(102, 126, 234, 0.25)'
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
              background: isDark
                ? 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)'
                : 'linear-gradient(135deg, #425f9e 0%, #667eea 100%)',
              color: 'white',
              borderRadius: '20px',
              px: 1.75,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.8rem',
              boxShadow: isDark
                ? '0 3px 12px rgba(0, 0, 0, 0.4)'
                : '0 3px 12px rgba(102, 126, 234, 0.35)'
            }}
          >
            Votre nom
            <ArrowDropDownIcon sx={{ fontSize: '1.2rem' }} />
          </Box>
          <Typography
            variant="body2"
            sx={{ color: isDark ? '#cfd8ea' : '#4a5568', lineHeight: 1.55 }}
          >
            Cliquez sur <b>votre nom en haut à droite</b>
            <NorthEastIcon sx={{ fontSize: '0.95rem', verticalAlign: 'middle', mx: 0.4, mb: 0.3 }} />
            pour naviguer dans l'application : rédaction, compte, guide…
          </Typography>
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            mt: 0.5,
            flexWrap: 'wrap',
            '& > *': { flex: '1 1 200px' }
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<MenuBookIcon />}
            onClick={handleGoToTutorial}
            sx={{
              borderRadius: '14px',
              py: 1.4,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #425f9e 0%, #667eea 100%)',
              boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #35508c 0%, #5468d4 100%)',
                boxShadow: '0 6px 18px rgba(102, 126, 234, 0.5)'
              }
            }}
          >
            Découvrir le tutoriel
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<DashboardIcon />}
            onClick={handleStayOnDashboard}
            sx={{
              borderRadius: '14px',
              py: 1.4,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(66, 95, 158, 0.5)',
              color: isDark ? '#e8ecf5' : '#425f9e',
              '&:hover': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.6)' : '#425f9e',
                background: isDark
                  ? 'rgba(255, 255, 255, 0.06)'
                  : 'rgba(102, 126, 234, 0.06)'
              }
            }}
          >
            Explorer par moi-même
          </Button>
        </Box>

        <Typography
          variant="caption"
          sx={{ textAlign: 'center', color: isDark ? '#9aa5bd' : '#8a94a8' }}
        >
          Le tutoriel reste accessible à tout moment via le menu, rubrique « Guide ».
        </Typography>
      </Box>
    </Dialog>
  );
}

export default WelcomeDialog;
